import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Bell, Check, Archive, Trash2, ArchiveRestore, MailOpen, X } from "lucide-react";

export default function NotificationsPage() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  // Axios config that treats 404 as a legitimate outcome for notification
  // mutations. Reason: a row may have been read/archived/deleted from another
  // tab, by the 30s auto-refresh cross-over, or by an admin action just before
  // the click. Rejecting on 404 would flood the console with red Axios errors
  // while the UX is already correct. This wrapper turns 404 into a resolved
  // response so nothing ever bubbles up as a runtime error.
  const AX_OK = { validateStatus: (s) => (s >= 200 && s < 300) || s === 404 };

  useEffect(() => { if (!loading && !user) nav("/auth"); }, [user, loading, nav]);

  const audience = user?.role === "admin" || user?.role === "moderator" ? "admin" : "user";

  const load = async () => {
    try {
      const params = { audience, limit: 200 };
      if (tab === "unread") params.unread_only = true;
      const { data } = await api.get("/notifications", { params, ...AX_OK });
      const safe = data && typeof data === "object" && Array.isArray(data.items) ? data : { items: [], unread: 0 };
      let list = safe.items;
      if (tab === "archived") list = list.filter((n) => n.archived);
      else list = list.filter((n) => !n.archived);
      setItems(list);
      setUnread(safe.unread || 0);
    } catch { toast.error(t("generic_error")); }
  };
  useEffect(() => { if (user) load(); setSelected(new Set()); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [user, tab]);
  useEffect(() => { if (!user) return; const iv = setInterval(load, 30000); return () => clearInterval(iv); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [user, tab]);

  // ---- Bulk selection (Lotto 3 #8) ----
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const visibleIds = useMemo(() => items.map((n) => n.notif_id), [items]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allVisibleSelected ? new Set() : new Set(visibleIds));
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/notifications/bulk-delete", { notif_ids: ids });
      toast.success(t("bulk_deleted_ok").replace("{n}", String(data.deleted ?? ids.length)));
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setBulkBusy(false); }
  };
  const bulkUpdate = async (patchObj) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/notifications/bulk-update", { notif_ids: ids, ...patchObj });
      toast.success(t("bulk_updated_ok").replace("{n}", String(data.modified ?? ids.length)));
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setBulkBusy(false); }
  };

  // Unified mutation wrapper. Every notification API call is invoked with
  // validateStatus that accepts 404 → we then inspect res.status and treat
  // 404 as a silent "already gone": drop the row, reload the list, do NOT
  // toast an error. Real failures (500, network, 4xx other than 404) surface
  // a discreet toast without triggering the React error overlay.
  const safeMutate = async (call, notifId) => {
    try {
      const res = await call();
      const status = res && typeof res === "object" ? res.status : 200;
      if (status === 404 && notifId) {
        setItems((arr) => arr.filter((x) => x.notif_id !== notifId));
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    }
  };

  const patch = (id, upd) => safeMutate(() => api.patch(`/notifications/${id}`, upd, AX_OK), id);
  const del = (id) => safeMutate(() => api.delete(`/notifications/${id}`, AX_OK), id);
  const markAllRead = () => safeMutate(() => api.post("/notifications/mark-all-read", null, { params: { audience }, ...AX_OK }), null);
  const openItem = async (n) => {
    if (!n.read) {
      // Fire-and-forget mark-as-read on open. 404 is silently accepted (the
      // notification may have vanished between render and click). No console
      // noise, no toast, no red overlay.
      try { await api.patch(`/notifications/${n.notif_id}`, { read: true }, AX_OK); }
      catch { /* network-level failure only — deliberately silent on open */ }
    }
    if (n.link) nav(n.link);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="notifications-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="notif-back-btn" />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold flex items-center gap-2"><Bell size={12} /> {t("notifications")}</div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mt-2">{t("notifications")} <span className="text-slate-400 font-normal text-lg">({unread})</span></h1>
          </div>
          <Button data-testid="notif-page-all-read" onClick={markAllRead} variant="outline" className="rounded-full hover:bg-emerald-50 hover:text-emerald-800"><Check size={14} className="mr-2" /> {t("notif_all_read")}</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-full">
            <TabsTrigger value="all" className="rounded-full" data-testid="notif-tab-all">{t("notif_tab_all") || "Tutte"}</TabsTrigger>
            <TabsTrigger value="unread" className="rounded-full" data-testid="notif-tab-unread">{t("notif_tab_unread") || "Non lette"}</TabsTrigger>
            <TabsTrigger value="archived" className="rounded-full" data-testid="notif-tab-archived">{t("notif_tab_archived") || "Archiviate"}</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-4">
            {/* Bulk toolbar — visible only when there are rows in the current tab. */}
            {items.length > 0 && (
              <div className="mb-3 sticky top-0 z-10 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-2xl px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="bulk-notif-toolbar">
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="bulk-notif-select-all"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  {t("bulk_select_all")}
                </label>
                <span className="text-xs text-slate-500 tabular-nums" data-testid="bulk-notif-count">
                  {t("bulk_selected_of").replace("{n}", String(selected.size)).replace("{total}", String(items.length))}
                </span>
                {selected.size > 0 && (
                  <>
                    <div className="mx-1 h-4 w-px bg-slate-200" />
                    <Button size="sm" variant="outline" data-testid="bulk-notif-read" disabled={bulkBusy} onClick={() => bulkUpdate({ read: true })} className="rounded-full text-xs h-8 hover:bg-emerald-50 hover:text-emerald-800">
                      <Check size={13} className="mr-1" /> {t("bulk_notif_mark_read")}
                    </Button>
                    <Button size="sm" variant="outline" data-testid="bulk-notif-archive" disabled={bulkBusy} onClick={() => bulkUpdate({ archived: true })} className="rounded-full text-xs h-8 hover:bg-slate-900 hover:text-white">
                      <Archive size={13} className="mr-1" /> {t("bulk_notif_archive")}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="bulk-notif-delete" disabled={bulkBusy} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200">
                          <Trash2 size={13} className="mr-1" /> {t("delete")}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="bulk-notif-delete-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("bulk_delete_confirm_title")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("bulk_delete_confirm_body").replace("{n}", String(selected.size))}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="bulk-notif-delete-cancel">{t("cancel")}</AlertDialogCancel>
                          <AlertDialogAction data-testid="bulk-notif-delete-confirm" onClick={bulkDelete} className="bg-red-600 hover:bg-red-700 text-white">
                            {t("delete")} ({selected.size})
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <button data-testid="bulk-notif-clear" onClick={clearSelection} className="text-xs text-slate-500 hover:text-slate-900 px-2 h-8 rounded-full hover:bg-slate-100 inline-flex items-center gap-1">
                      <X size={12} /> {t("bulk_clear_selection")}
                    </button>
                  </>
                )}
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              {items.length === 0 ? (
                <div className="p-10 text-center text-slate-500">{t("notif_empty")}</div>
              ) : items.map((n) => (
                <div key={n.notif_id} className={`p-4 border-b border-slate-100 flex items-start gap-3 ${!n.read ? "bg-emerald-50/30" : ""} ${selected.has(n.notif_id) ? "bg-emerald-50/60" : ""}`} data-testid={`notif-item-${n.notif_id}`}>
                  <label className="pt-1 shrink-0 cursor-pointer" title={t("bulk_select_row")}>
                    <input
                      type="checkbox"
                      data-testid={`notif-select-${n.notif_id}`}
                      checked={selected.has(n.notif_id)}
                      onChange={() => toggleOne(n.notif_id)}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </label>
                  <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${!n.read ? "bg-emerald-500" : "bg-slate-200"}`} />
                  <button onClick={() => openItem(n)} className="flex-1 text-left min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t(`notif_type_${n.type}`) || n.type}</div>
                    <div className="text-sm text-slate-900 font-semibold break-words">{n.title}</div>
                    {n.body && <div className="text-xs text-slate-600 mt-0.5 break-words">{n.body}</div>}
                    <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </button>
                  <div className="flex flex-col gap-1">
                    {n.read ? (
                      <button data-testid={`notif-unread-${n.notif_id}`} onClick={() => patch(n.notif_id, { read: false })} className="w-8 h-8 rounded-full hover:bg-emerald-50 text-emerald-700 flex items-center justify-center" title={t("notif_mark_unread")}><MailOpen size={14} /></button>
                    ) : (
                      <button data-testid={`notif-read-${n.notif_id}`} onClick={() => patch(n.notif_id, { read: true })} className="w-8 h-8 rounded-full hover:bg-emerald-50 text-emerald-700 flex items-center justify-center" title={t("notif_mark_read")}><Check size={14} /></button>
                    )}
                    <button data-testid={`notif-archive-${n.notif_id}`} onClick={() => patch(n.notif_id, { archived: !n.archived })} className="w-8 h-8 rounded-full hover:bg-slate-100 text-slate-600 flex items-center justify-center" title={t("notif_archive")}>
                      {n.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    </button>
                    <button data-testid={`notif-delete-${n.notif_id}`} onClick={() => del(n.notif_id)} className="w-8 h-8 rounded-full hover:bg-red-50 text-red-600 flex items-center justify-center" title={t("notif_delete")}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
